/* eslint-disable react-hooks/exhaustive-deps */
// @flow
import React, {useEffect, useState, useRef} from "react";
import InfoBox from "./InfoBox";
import PriceBox, {AvailabilityLabel} from "./PriceBox";
import Loading from "../layout/Loading";
import type {Product} from "../../models/Product";
import type {Attachments} from "../../models/Attachments";
import Gallery from "./Gallery";
import type {translate} from "../../i18next";
import SwimLane from "../SwimLane";
import {CSSTransition} from "react-transition-group";
import Share from "./Sharing/Share";
import SharingIconBox from "./Sharing/SharingIconBox";
import "./Detailpage.scss";
import QuestionFormContainer from "../../container/productDetail/QuestionForm/QuestionFormContainer";
import NscDealerSearchContainer from "../../container/productDetail/NSC/NscDealerSearchContainer";
import {DealerList} from "./NSC/DealerList";
import type {DealerDetails} from "../../models/DealerDetails";
import EventEmitter from "../../helper/eventEmitter";
import {scrollToTop} from "../../helper/anchor";
import type {GroupMember} from "../../models/GroupMember";
import PickupLocationContainer from "../../container/pickupLocation/PickupLocationContainer";
import WelcomeMessageModal from "../layout/WelcomeMessageModal";
import GridElementContainer from "../../container/gridPages/GridElementContainer";
import type {Contacts} from "../../models/DetailedProduct";
import stringUtils from "../../helper/stringUtils";
import Button from "../layout/Button";
import history from "../../history";
import {CartReducerState} from "../../reducers/cartReducer";
import PaymentApi from "../../api/PaymentApi";
import OrderProcessingApi from "../../api/OrderProcessingApi";
import {currencyConverter} from "../../helper/currency";
import i18n from "../../i18next";
import usePayPalScript from "../../hooks/usePayPalScript";
import {PAYMENT_METHODS} from "../../models/PaymentRequests";
import RootNodeProvider from "../../helper/RootNodeProvider";
import {emitInternalEvent, toProductDetails} from "../../helper/tracker/internalEvents";
import WarrantyLabel from "../empco/WarrantyLabel";

const isGpsrDetailsEnabled = () =>
    RootNodeProvider.getRootNode()?.dataset.enableGpsrDetails !== "false";

export type DetailpageProps = {
    location: any,
    articleNumber: string,
    product: Product,
    attachments: Attachments,
    properties: any,
    productLoaded: ?boolean,
    config: any,
    getProduct: string => Product,
    isBookmarked: ?boolean,
    cart: CartReducerState;
    addToCart: (Product, string) => void,
    addBookmark: Product => void,
    removeBookmark: Product => void,
    t: translate,
    isShopEnabled: boolean,
    isLeadModuleEnabled: boolean,
    watchlistEnabled: boolean,
    relatedProducts: Product[],
    isNSCDealer: boolean,
    dealerDetails: DealerDetails,
    showDealerList: boolean,
    setShowDealerList: boolean => void,
    trackShareButtonClick: Product => void,
    pickupLocation: ?GroupMember,
    locationChoices: ?GroupMember[],
    needsPickupLocations: boolean,
    showWelcomeMessage: boolean,
    resetWelcomeMessage: () => void,
    brandName: string,
    contacts: Contacts,
    loadPaypalClientId: () => void,
    showMsrp: boolean,
}

const Detailpage = (props: DetailpageProps) => {
    const [currentProduct, setCurrentProduct] = useState(props.articleNumber);
    const [showQuestion, setShowQuestion] = useState(false);
    const [showShareForm, setShowShareForm] = useState(false);
    const [showShareIconBox, setShowShareIconBox] = useState(false);
    const [isProductAddedToCart, setIsProductAddedToCart] = useState(false);
    const [orderPayments, setOrderPayments] = useState([]);
    const [calcOrderResponse, setCalcOrderResponse] = useState(null);
    const successBoxRef = useRef(null);

    const {scriptResolved, isPaymentMethodAvailable} = usePayPalScript("capture");

    useEffect(() => {
        if (isProductAddedToCart && successBoxRef.current) {
            const rect = successBoxRef.current.getBoundingClientRect();
            const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;

            if (!isInViewport) {
                successBoxRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
            }
        }
    }, [isProductAddedToCart]);

    useEffect(() => {
        if (isProductAddedToCart && props.dealerDetails) {
            props.loadPaypalClientId(props.dealerDetails.dealership.dealerId);

            const paymentApi = new PaymentApi();
            paymentApi.getPaymentMethods(props.dealerDetails.dealership.dealerId)
                .then(response => {
                    setOrderPayments(response.paymentMethods);
                })
                .catch(error => {
                    console.error("Error fetching order payments:", error);
                });

            const orderProcessingApi = new OrderProcessingApi();

            // the chosen mounting location provides the hourly rate - same as checkCart() does in the checkout
            const assembler = props.pickupLocation ? {index: props.pickupLocation.cfgKey} : null;

            const cartValidationRequest = {
                dealerNo: {
                    index: props.dealerDetails.dealership.dealerId
                },
                assembler,
                items: props.cart.items.map(item => ({
                    ...item,
                    product: {
                        dealerId: props.dealerDetails.dealership.dealerId,
                        productNo: item.product.articleNumber,
                        purchaseOrderNo: item.product.id
                    },
                    shippingMethod: {
                        code: item.shippingMethod
                    },
                    amount: {
                        amount: item.amount,
                        unit: {
                            code: "piece"
                        }
                    }
                }))
            };

            orderProcessingApi.calcOrder(cartValidationRequest)
                .then(response => {
                   setCalcOrderResponse(response);
                });
        }
    }, [currentProduct, isProductAddedToCart, props.dealerDetails, props.pickupLocation, scriptResolved]);

    useEffect(() => {
        setIsProductAddedToCart(false);
    }, [props.product]);

    useEffect(() => {
        props.getProduct(props.articleNumber);
        new EventEmitter().emitEvent("HPM_PAGE_LOADED");
        scrollToTop();
    }, []);

    useEffect(() => {
        if (currentProduct !== props.articleNumber) {
            new EventEmitter().emitEvent("HPM_PAGE_LOADED");
            props.getProduct(props.articleNumber);
            setCurrentProduct(props.articleNumber);
            scrollToTop();
        }
    }, [currentProduct, props.articleNumber, props.getProduct]);

    useEffect(() => {
        return () => {
            if (props.setShowDealerList) {
                props.setShowDealerList(false);
            }
        };
    }, [props.setShowDealerList]);

    const renderCrossPromotion = () => {
        if (!props.relatedProducts.length) {
            return null;
        }

        return <React.Fragment>
            <div className="hpm-categoryProduct">
                <div className="hpm-ProductTitle">{props.t("productDetail.relatedArticle")}</div>
            </div>
            <SwimLane
                id={"related_products"}
                setAllowsClick={false}
            >
                {
                    props.relatedProducts.map(product => {
                        return <GridElementContainer key={product.id} product={product}/>;
                    })
                }
            </SwimLane>
        </React.Fragment>;
    };

    const toggleQuestion = () => {
        if (!showQuestion) {
            emitInternalEvent("product_inquiry_started", {product: toProductDetails(props.product)});
        }
        setShowQuestion(!showQuestion);
        setShowShareForm(false);
        props.setShowDealerList(false);
    };

    const setDealerList = (value: boolean) => {
        setShowQuestion(false);
        setShowShareForm(false);
        props.setShowDealerList(value);
    };

    const toggleSharingForm = () => {
        if (navigator.share) return;
        setShowQuestion(false);
        setShowShareForm(!showShareForm);
        props.setShowDealerList(false);
    };

    const toggleSharingIconBox = () => {
        setShowShareIconBox(!showShareIconBox);
    };

    const handleShare = () => {
        const shareData = {
            title: window.document.title,
            text: props.product.name,
            url: window.location
        };
        navigator.share(shareData)
            .then(() => props.trackShareButtonClick(props.product))
            /* eslint-disable-next-line */
            .catch(error => console.log(error));
    };

    const renderShareBtn = () => {
        return navigator.share
            ? <div className="hpm-shareWrapper">
                <div
                    onClick={handleShare}
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                            handleShare();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    {props.t("feedback.share")}
                </div>
            </div>
            : <div className="hpm-shareWrapper">
    <span
        onClick={toggleSharingIconBox}
        onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
                toggleSharingIconBox();
            }
        }}
        role="button"
        tabIndex={0}
        className="hpm-sharingLink"
    >
     {props.t("feedback.share")}
        {showShareIconBox && <SharingIconBox
            toggleSharingForm={toggleSharingForm}
            trackShareButtonClick={props.trackShareButtonClick}
            product={props.product}
        />}
    </span>
            </div>;
    };

    const renderContactButton = () => {
        return <div className={"contactWrapper"}>
            <div className={props.dealerDetails.phone ? "hpm-phone" : ""}>
                <a href={props.dealerDetails.phone
                    ? `tel:${props.dealerDetails.phone.replace(/ /g, "")}`
                    : ""}
                   onClick={() => props.dealerDetails.phone && emitInternalEvent("call_dealer", {
                       product: toProductDetails(props.product),
                       phone: `tel:${props.dealerDetails.phone.replace(/ /g, "")}`,
                   })}
                >
                    {props.dealerDetails.phone
                        ? stringUtils.makeSpacesNonBreaking(props.dealerDetails.phone)
                        : ""
                    }
                </a>
            </div>
        </div>;
    };

    const addToCart = shippingMethod => {
        props.addToCart(props.product, shippingMethod);
        setIsProductAddedToCart(true);
    };

    const renderProductAddedToCart = () => {
        const productInCart = props.cart.items.find(item => item.product.id === props.product.id);

        const amount = props.cart.items.reduce((acc, item) => {
            return acc + parseInt(item.amount);
        }, 0);

        const renderAvailabilityLabel = (shippingMethod) => {
            return (
                <AvailabilityLabel
                    t={props.t}
                    product={productInCart.product}
                    selectedOption={shippingMethod}
                    needsPickupLocation={props.needsPickupLocations}
                    isShopEnabled={props.isShopEnabled}
                />
            );
        };

        const isPayPalMethodAvailable = (paymentMethod) => {
            return isPaymentMethodAvailable(paymentMethod);
        }

        const renderPaymentMethod = (paymentMethod) => {
            switch (paymentMethod) {
                case PAYMENT_METHODS.PAYPAL:
                    return isPayPalMethodAvailable(paymentMethod) && <span key={paymentMethod} title={props.t("cart.paymentMethods.paypal")} className={"hpm-paymentcarts hpm-paypal " + i18n.language.slice(0, 2)}/>;
                case PAYMENT_METHODS.PAY_LATER:
                    return isPayPalMethodAvailable(paymentMethod) && <span key={paymentMethod} title={props.t("cart.paymentMethods.pay-later")} className={"hpm-paymentcarts hpm-pay-later " + i18n.language.slice(0, 2)}/>;
                case PAYMENT_METHODS.CARD:
                    return isPayPalMethodAvailable(paymentMethod) && <span key={paymentMethod} title={props.t("cart.paymentMethods.card")} className={"hpm-paymentcarts hpm-card " + i18n.language.slice(0, 2)}/>;
                case PAYMENT_METHODS.SEPA:
                    return isPaymentMethodAvailable(paymentMethod) && <span key={paymentMethod} title={props.t("cart.paymentMethods.sepa")} className={"hpm-paymentcarts hpm-sepa " + i18n.language.slice(0, 2)}/>;
                case PAYMENT_METHODS.PRZELEWY24:
                    return isPayPalMethodAvailable(paymentMethod) && <span key={paymentMethod} title={props.t("cart.paymentMethods.przelewy24")} className={"hpm-paymentcarts hpm-przelewy24 " + i18n.language.slice(0, 2)}/>;
                case PAYMENT_METHODS.PREPAID:
                    return <span key={paymentMethod} title={props.t("cart.paymentMethods.prepaid")} className={"hpm-paymentcarts hpm-bankWire " + i18n.language.slice(0, 2)}/>;
                case PAYMENT_METHODS.CASH:
                    return <span key={paymentMethod} title={props.t("cart.paymentMethods.cash")} className={"hpm-paymentcarts hpm-euro " + i18n.language.slice(0, 2)}/>;
                default:
                    return null;
            }
        };

        return <div className="hpm-productPage" data-testid="productPage">
            <div className={"hpm-cartSuccess"}>
                <div className={"hpm-successBox"} ref={successBoxRef}>
                    <span>{props.t("cart.productAddedToCart")}</span>
                </div>
                <div className={"hpm-summary"}>
                    <img src={props.product.galleryImage.attachmentUrl} alt={props.product.galleryImage.caption}/>
                    <div>
                        <span className={"hpm-title"}>{productInCart.product.name}</span>
                        <span className={"hpm-availability"}>
                                {renderAvailabilityLabel(productInCart.shippingMethod)}
                        </span>
                        <span className={"hpm-amount"}>{props.t("cart.quantity")} {productInCart.amount}</span>
                    </div>
                </div>
                <div className="hpm-cartSuccessWarranty">
                    <WarrantyLabel product={productInCart.product} euShop={props.config.euShop} t={props.t} variant="compact"/>
                </div>
                <hr/>
                <div className={"hpm-cart"}>
                    <div>
                        <span>{props.t("cart.cartWithItems", {count: amount})}</span>
                        <span>{calcOrderResponse && currencyConverter({
                            value: calcOrderResponse.orderItems.orderItemsPriceSum.cost,
                            currency: calcOrderResponse.orderItems.orderItemsPriceSum.currency.code
                        })}</span>
                    </div>
                    <Button name={"checkout"} onClick={() => {
                        if (history.location.pathname.includes("/checkout")) {
                            window.history.back();
                        } else {
                            (history).pushCheckout();
                        }
                    }}>
                        {props.t("cart.goToCart")}
                    </Button>
                    <Button name="continue" onClick={() => {
                        history.pushHome();
                    }}>{props.t("cart.continueShopping")}</Button>
                </div>
            </div>
            {scriptResolved &&
                <div className={"hpm-payment"}>
                    <div className="hpm-categoryProduct">
                        <div className="hpm-ProductTitle">{props.t("cart.paymentMethodsTitle")}</div>
                    </div>
                    <div className={""}>
                        {orderPayments.map(paymentMethod => renderPaymentMethod(paymentMethod.paymentMethod))}
                    </div>
                </div>
            }
            {renderCrossPromotion()}
        </div>;
    };

    if (props.productLoaded) {
        if (isProductAddedToCart) {
            return renderProductAddedToCart();
        }

        return (
            <div className="hpm-productPage" data-testid="productPage">
                <WelcomeMessageModal
                    dealerName={props.dealerDetails.name}
                    dealerImageURL={props.dealerDetails.garagePictureUrl}
                    fallbackImageURL={props.fallbackImageURL}
                    showWelcomeMessage={props.showWelcomeMessage}
                    resetWelcomeMessage={props.resetWelcomeMessage}
                    t={props.t}
                />
                <CSSTransition in={!!showShareForm} unmountOnExit={true} timeout={0}>
                    <React.Fragment>
                        <Share product={props.product} closeSharePage={toggleSharingForm}/>
                    </React.Fragment>
                </CSSTransition>
                <CSSTransition in={!!showQuestion} unmountOnExit={true} timeout={0}>
                    <React.Fragment>
                        <QuestionFormContainer product={props.product} closeQuestion={toggleQuestion}/>
                    </React.Fragment>
                </CSSTransition>
                <CSSTransition in={!!props.showDealerList} unmountOnExit={true} timeout={0}>
                    <React.Fragment>
                        <DealerList
                            t={string => string} product={props.product}
                            theme={props.config.theme}
                            onClose={() => setDealerList(false)}
                        />
                    </React.Fragment>
                </CSSTransition>
                <CSSTransition
                    in={!showQuestion && !showShareForm && !props.showDealerList}
                    unmountOnExit={true}
                    timeout={0}>
                    <React.Fragment>
                        <Gallery
                            galleryImage={props.product.galleryImage}
                            images={props.attachments.images}
                            videos={props.attachments.videos}
                        />
                        <div className="hpm-sideBar">
                            <div className="hpm-sideBarTitle">{props.product.name}</div>
                            <div className="hpm-sideBarArticleNumber">
                                <span className="hpm-ArticleNumberLabel">
                                    {props.t("productDetail.articleNumber")}:
                                </span>
                                <span className="hpm-ArticleNumberValue">
                                    {props.product.articleNumber || "-"}
                                </span>
                            </div>
                            {props.pickupLocation && (
                                <div className="hpm-selectedLocation">
                                    <div className="hpm-locationIcon"/>
                                    <div>
                                        {`${props.pickupLocation.address.street}\u00A0${props.pickupLocation.address.streetNumber}, ${props.pickupLocation.address.postalCode}\u00A0${props.pickupLocation.address.town} `}
                                        <PickupLocationContainer/>
                                    </div>
                                </div>
                            )}
                            {props.isNSCDealer
                                ? <>
                                    <NscDealerSearchContainer
                                        setDealerList={setDealerList}
                                        showDealerList={props.showDealerList}/>
                                    <div className="hpm-shareAndContact">
                                        {renderShareBtn()}
                                    </div>
                                </>
                                : <>
                                    <PriceBox
                                        t={props.t}
                                        isShopEnabled={props.isShopEnabled}
                                        isLeadModuleEnabled={props.isLeadModuleEnabled}
                                        watchlistEnabled={props.watchlistEnabled}
                                        infobox={props.config.disclaimer}
                                        addToCart={addToCart}
                                        product={props.product}
                                        dealerDetails={props.dealerDetails}
                                        addBookmark={props.addBookmark}
                                        isBookmarked={props.isBookmarked}
                                        removeBookmark={props.removeBookmark}
                                        needsPickupLocation={props.needsPickupLocations}
                                        showMsrp={props.showMsrp}
                                    />
                                    <div className="hpm-shareAndContact">
                                        {renderShareBtn()}
                                        {(props.watchlistEnabled || props.isShopEnabled) && renderContactButton()}
                                        {props.isLeadModuleEnabled &&
                                            <button
                                                onClick={toggleQuestion}
                                                onKeyDown={e => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        toggleQuestion();
                                                    }
                                                }}
                                                type={"button"}
                                                tabIndex={0}
                                                className="hpm-questionWrapper"
                                            >
                                                <div>{props.t("feedback.questions")}</div>
                                            </button>
                                        }
                                    </div>
                                </>
                            }
                        </div>
                        <InfoBox
                            t={props.t}
                            datas={props.properties}
                            files={props.attachments.documents}
                            product={props.product}
                            brandName={props.brandName}
                            contacts={props.contacts}
                            gpsrDetailsEnabled={isGpsrDetailsEnabled()}
                        />
                        <WarrantyLabel product={props.product} euShop={props.config.euShop} t={props.t}/>
                        {renderCrossPromotion()}
                    </React.Fragment>
                </CSSTransition>
            </div>
        );
    } else {
        return (
            <Loading/>
        );
    }
};

export default Detailpage;
